"use client";

import Image from "next/image";
import { useEffect, useRef } from "react";
import { FaArrowLeftLong } from "react-icons/fa6";
import Link from "next/link";
import { getFamilyContentOption } from "../enums/family-guide-content";
import { getFamilyGuideMeasureOption } from "../enums/family-guide-measure";

export default function UserManual({ initialItem }: { initialItem: any }) {
	const item = initialItem;
	const contentRef = useRef<any>(null);
	const backgroundRef = useRef<any>(null);

	const updateBackgroundHeight = () => {
		if (contentRef.current && backgroundRef.current) {
			const contentHeight = contentRef.current?.offsetHeight;
			backgroundRef.current.style.height = `${contentHeight}px`;
		}
	};

	const groupedFamilyGuides = item?.familyGuideList?.reduce(
		(acc: any, current: any) => {
			const key = `${current.content}-${current.measure}`;
			if (!acc[key]) {
				acc[key] = {
					content: current.content,
					measure: current.measure,
					descriptions: [],
				};
			}
			acc[key].descriptions.push(current.description);
			return acc;
		},
		{},
	);

	// Set initial height and add resize listener
	useEffect(() => {
		// Initial height adjustment
		updateBackgroundHeight();

		// Update height on window resize
		window.addEventListener("resize", updateBackgroundHeight);

		// Clean up
		return () => {
			window.removeEventListener("resize", updateBackgroundHeight);
		};
	}, []);

	return (
		<>
			<div
				ref={backgroundRef}
				className="absolute -z-10 w-full top-0"
				style={{
					backgroundImage: `url(${initialItem.imageUrl}),
                      linear-gradient(to bottom, transparent 80%, rgba(0, 0, 0, 1)),
                      linear-gradient(to right, transparent 30%, rgba(0, 0, 0, 1)),
                      linear-gradient(to left, transparent 30%, rgba(0, 0, 0, 1))
                    `,
					backgroundRepeat: "no-repeat",
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundBlendMode: "overlay",
					filter: "blur(16px)",
				}}
			></div>
			<div className="w-full text-white py-6 px-6 lg:px-[80px] flex flex-col items-start">
				<div
					ref={contentRef}
					className="w-full py-6 rounded-lg shadow-lg flex flex-col-reverse justify-between lg:flex-row gap-4"
				>
					{/* Movie Details */}
					<div className="lg:w-2/3 py-6 flex flex-col gap-4">
						<div className={"flex items-start justify-start flex-col w-full"}>
							<h1 className="text-[35px] md:text-[48px] font-extraBold">
								راهنمای خانواده
							</h1>
							<div className={"mt-7"}>
								<h2 className=" text-[24px] font-normal">{item?.title}</h2>
								<p className={"text-[18px] font-normal"}>{item?.description}</p>
							</div>

							{item?.familyGuideList?.length > 0 ? (
								<>
									<h3 className=" text-[24px] font-bold my-4">
										رتبه بندی محتوا
									</h3>
									<div className={"flex flex-col gap-2 w-full"}>
										{Object.values(groupedFamilyGuides).map(
											(guide: any, index: number) => {
												const content = getFamilyContentOption(guide.content);
												const measure = getFamilyGuideMeasureOption(
													guide.measure,
												);
												return (
													<Link
														key={index}
														href={"#" + content?.value}
														className={`flex flex-row items-center justify-between text-[18px] py-3 px-3 font-medium flex-grow ${measure?.bgColor} ${measure?.color} bg-opacity-10 text-${guide.color} rounded-md hover:opacity-70`}
													>
														<span>
															{content?.label}:{measure?.label}
														</span>
														<FaArrowLeftLong />
													</Link>
												);
											},
										)}
									</div>
								</>
							) : (
								<h3 className=" text-[24px] font-bold my-4">
									هیچ راهنمایی برای این محتوا وجود ندارد!
								</h3>
							)}
						</div>
					</div>

					{/* Movie Poster */}
					<div className="w-full lg:w-[376px] flex justify-center flex-col items-center lg:ml-20">
						<div className={"w-full lg:w-[376px] max-w-[400px]"}>
							<Image
								src={
									process.env.NEXT_PUBLIC_FILE_URL + item?.backgroundImage?.code
								}
								alt={item?.title}
								width={376}
								height={526}
								layout="responsive"
								className={`m-auto ${
									item.cutinSuggested ? "rounded-t-lg" : "rounded-lg"
								}`}
							/>
						</div>
					</div>
				</div>
				{Object.values(groupedFamilyGuides).map((guide: any, index: number) => {
					const content = getFamilyContentOption(guide.content);
					const measure = getFamilyGuideMeasureOption(guide.measure);
					return (
						<div
							className={"flex flex-col gap-4 scroll-mt-[70px] py-2 w-full"}
							key={index}
							id={content?.value}
						>
							<h3 className={`text-[24px] ${measure?.color}`}>
								{content?.label}
							</h3>
							{guide.descriptions.map((desc: string, descIndex: number) => (
								<div key={descIndex}>
									<p className={"text-[17px] text-gray-300"}>{desc}</p>
									{descIndex < guide.descriptions.length - 1 && (
										<hr className="mt-4 border-gray-700" />
									)}
								</div>
							))}
						</div>
					);
				})}
			</div>
		</>
	);
}
